import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Contact from './pages/Contact';
import ServicesPage from './pages/ServicesPage';
import ServiceDetail from './pages/ServiceDetail';
import About from './pages/About';
import NotFound from './pages/NotFound';
import SEO from './components/SEO';
import './App.css';

// Create a wrapper component to handle route transitions
function AppContent() {
  const location = useLocation();
  const { t } = useTranslation();
  const [overlayGone, setOverlayGone] = useState(false);
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  
  // Use this ref to track if the initial load has completed
  const initialLoadComplete = useRef(false);
  // Use this ref to track the previous pathname for actual route changes
  const prevPathname = useRef(location.pathname);

  // Handle initial font loading
  useEffect(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setOverlayGone(true);
        setTimeout(() => {
          initialLoadComplete.current = true;
        }, 200);
      }, 200);
    });
  }, []);

  // Handle route changes
  useEffect(() => {
    if (initialLoadComplete.current && location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname;
      setIsRouteChanging(true);
      setOverlayGone(false);
      setTimeout(() => {
        setOverlayGone(true);
      }, 150);
    }
  }, [location.pathname]);

  // Handle initial page load
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      const handleBeforeUnload = () => {
        window.scrollTo(0, 0);
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, []);

  return (
    <div className="App w-full min-h-screen flex flex-col">
      {(!overlayGone || isRouteChanging) && (
        <div 
          className={`fixed top-0 left-0 w-full h-full bg-white z-50 transition-opacity duration-500 ${
            overlayGone ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`} 
          onTransitionEnd={() => {
            if (overlayGone && isRouteChanging) {
              setIsRouteChanging(false);
            }
          }}
        />
      )}

      <Header />
      <main className="flex-grow">
        <Routes>
          {/* Redirects */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/diensten" element={<Navigate to="/services" replace />} />
          
          {/* Main Routes */}
          <Route path="/" element={
            <>
              <SEO 
                title="AlzijDigi | Op Maat Gemaakte Software & Digitale Diensten"
                description="Transformeer uw bedrijf met AlzijDigi's expert digitale oplossingen. Wij zijn gespecialiseerd in op maat gemaakte software ontwikkeling, webapplicaties en digitale marketing diensten."
                canonicalUrl="/"
              />
              <Home fontsReady={overlayGone} />
            </>
          } />
          
          <Route path="/contact" element={
            <>
              <SEO 
                title="AlzijDigi | Neem Contact Op"
                description="Neem contact op met AlzijDigi voor op maat gemaakte software ontwikkeling, webapplicaties en digitale oplossingen. Laten we bespreken hoe we uw bedrijf kunnen laten groeien."
                canonicalUrl="/contact"
              />
              <Contact />
            </>
          } />
          
          <Route path="/services" element={
            <>
              <SEO 
                title="AlzijDigi | Onze Diensten"
                description="Ontdek AlzijDigi's uitgebreide digitale diensten inclusief op maat gemaakte software ontwikkeling, webapplicaties, mobiele apps en digitale marketing oplossingen."
                canonicalUrl="/services"
              />
              <ServicesPage />
            </>
          } />
          
          <Route path="/services/:serviceSlug" element={
            <>
              <SEO 
                title={`AlzijDigi | ${t(`service_${location.pathname.split('/').pop()}_title`)}`}
                description={t(`service_${location.pathname.split('/').pop()}_description`)}
                canonicalUrl={`/services/${location.pathname.split('/').pop()}`}
              />
              <div className="container mx-auto px-4 mt-24 mb-16">
                <h1 className="text-4xl font-bold mb-8">
                  {t(`service_${location.pathname.split('/').pop()}_title`)}
                </h1>
                <div className="prose max-w-none">
                  <p className="text-lg mb-8">
                    {t(`service_${location.pathname.split('/').pop()}_description`)}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                    <div>
                      <h2 className="text-2xl font-bold mb-4">{t('service_features')}</h2>
                      <ul className="list-disc pl-5 space-y-2">
                        {[1, 2, 3, 4].map((num) => (
                          <li key={num}>
                            {t(`service_${location.pathname.split('/').pop()}_feature_${num}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold mb-4">{t('service_benefits')}</h2>
                      <ul className="list-disc pl-5 space-y-2">
                        {[1, 2, 3, 4].map((num) => (
                          <li key={num}>
                            {t(`service_${location.pathname.split('/').pop()}_benefit_${num}`)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </>
          } />
          
          <Route path="/over-ons" element={
            <>
              <SEO 
                title="AlzijDigi | Over Ons"
                description="Leer meer over AlzijDigi, een toonaangevend digitaal bureau in Nederland. Wij combineren technische expertise met creatieve innovatie om uitzonderlijke digitale oplossingen te leveren."
                canonicalUrl="/over-ons"
              />
              <About />
            </>
          } />
          
          <Route path="/cases" element={
            <>
              <SEO 
                title="AlzijDigi | Onze Cases"
                description="Bekijk onze succesvolle projecten en cases. Ontdek hoe we bedrijven hebben geholpen met onze digitale oplossingen."
                canonicalUrl="/cases"
              />
              <div className="container mx-auto px-4 mt-24 mb-16">
                <h1 className="text-4xl font-bold mb-8">{t('cases_title')}</h1>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="bg-white rounded-lg shadow-lg p-6">
                      <h2 className="text-xl font-bold mb-4">{t(`case_${num}_title`)}</h2>
                      <p className="text-gray-600 mb-4">{t(`case_${num}_description`)}</p>
                      <div className="flex flex-wrap gap-2">
                        {['web', 'mobile', 'design'].map((tech) => (
                          <span key={tech} className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">
                            {t(`tech_${tech}`)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          } />
          
          <Route path="/privacy" element={
            <>
              <SEO 
                title="AlzijDigi | Privacy Policy"
                description="Lees onze privacy policy en ontdek hoe we uw gegevens beschermen en verwerken."
                canonicalUrl="/privacy"
              />
              <div className="container mx-auto px-4 mt-24 mb-16">
                <h1 className="text-4xl font-bold mb-8">{t('privacy_title')}</h1>
                <div className="prose max-w-none">
                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">{t('privacy_section_1_title')}</h2>
                    <p>{t('privacy_section_1_content')}</p>
                  </section>
                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">{t('privacy_section_2_title')}</h2>
                    <p>{t('privacy_section_2_content')}</p>
                  </section>
                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">{t('privacy_section_3_title')}</h2>
                    <p>{t('privacy_section_3_content')}</p>
                  </section>
                </div>
              </div>
            </>
          } />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LanguageProvider>
          <ScrollToTop />
          <AppContent />
        </LanguageProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;